/**
 * System Core Control Panel
 *
 * Unified "single pane of glass" for all system configuration:
 * - Active Gateways (Payment Gateways)
 * - Financial Settings (Refund Notifications & Uber Markup)
 * - Application Logic (App Settings)
 *
 * Features dark mode sci-fi HUD design with vertical tab navigation.
 *
 * @module SystemCore
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Switch,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
  Collapse,
  Divider,
} from '@mui/material';
import {
  CreditCard as PaymentIcon,
  Inventory2 as InventoryIcon,
  Tune as AppLogicIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  AccountBalance as BankIcon,
  QrCode as QrCodeIcon,
  GpsFixed as GpsIcon,
  QrCodeScanner as ScannerIcon,
  FiberManualRecord as StatusDot,
  SignalCellularAlt as SignalIcon,
  PhoneAndroid as CompanionIcon,
  Email as EmailIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Assignment as AuditIcon,
  Storefront as UberIcon,
  Block as RestrictedIcon,
  SystemUpdate as UpdateIcon,
} from '@mui/icons-material';
import config from '../../config';
import Logger from '../../utils/Logger';
import { invalidateAppSettings } from '../../hooks/useAppSettings';
import { useAdminTheme } from '../../admin-theme';
import { getValidToken } from '../../services/tokenRefreshService';

// ============================================================================
// Types
// ============================================================================

type TabId = 'financial' | 'inventory' | 'app';

interface PaymentGateway {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
  warningMessage?: string;
  type: 'card' | 'eft' | 'qr' | 'bnpl';
}

interface AppSettingsState {
  warnIfFarFromAddress: boolean;
  distanceThresholdKm: number;
  enableBarcodeScanner: boolean;
  enableCompanionApp: boolean;
  useLoopDelivery: boolean;
  /** Minutes a Loop booking may wait for a driver before Uber Direct is offered */
  uberFallbackAfterMinutes: number;
  /** Uber Direct zone: highest Uber quote (Rand incl. VAT) we deliver at; null = no cap */
  uberDirectPriceCap: number | null;
  /** Switch failed / driverless Loop orders to Uber Direct automatically */
  uberFallbackAuto: boolean;
  /** How many more times a Loop booking refused for "no drivers" is retried */
  loopNoCapacityRetries: number;
  /** Minutes between those retries (1-15) */
  loopNoCapacityRetryMinutes: number;
  /** Minutes to wait for the tracking WhatsApp to be delivered before sending the SMS */
  trackingSmsFallbackMinutes: number;
  /** Who sends SMS: 'twilio' | 'bulksms' (WhatsApp is always Twilio) */
  smsProvider: string;
  refundNotificationEmail: string;
  refundCcEmails: string[];
  enableAutoDearVoid: boolean;
  enableBarcodeAudit: boolean;
  auditIdleMinutes: number;
  auditMissionTimeoutHours: number;
  auditMissionSize: number;
  auditPrioritizePopular: boolean;
  auditSkipNoImage: boolean;
  showRestrictedBadge: boolean;
  minimumAppVersionIos: string;
  minimumAppVersionAndroid: string;
  appStoreUrlIos: string;
  appStoreUrlAndroid: string;
  forceUpdateMessage: string;
}

// ============================================================================
// Tab Navigation Component
// ============================================================================

interface NavTabProps {
  id: TabId;
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  isActive: boolean;
  onClick: () => void;
  glowColor: string;
}

function NavTab({ id, icon, label, sublabel, isActive, onClick, glowColor }: NavTabProps) {
  const { colors } = useAdminTheme();
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2.5,
        py: 2,
        cursor: 'pointer',
        position: 'relative',
        borderRadius: 2,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        bgcolor: isActive ? `${glowColor}15` : 'transparent',
        border: `1px solid ${isActive ? glowColor : 'transparent'}`,
        boxShadow: isActive ? `0 0 20px ${glowColor}40, inset 0 0 20px ${glowColor}10` : 'none',
        '&:hover': {
          bgcolor: isActive ? `${glowColor}20` : 'rgba(255, 255, 255, 0.03)',
          transform: 'translateX(4px)',
        },
      }}
    >
      {/* Active Indicator Line */}
      {isActive && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 3,
            height: '60%',
            bgcolor: glowColor,
            borderRadius: '0 2px 2px 0',
            boxShadow: `0 0 10px ${glowColor}`,
          }}
        />
      )}

      {/* Icon Container */}
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isActive ? `${glowColor}20` : colors.bgElevated,
          border: `1px solid ${isActive ? glowColor : colors.border}`,
          transition: 'all 0.3s',
          '& svg': {
            fontSize: 22,
            color: isActive ? glowColor : colors.textSecondary,
            filter: isActive ? `drop-shadow(0 0 6px ${glowColor})` : 'none',
          },
        }}
      >
        {icon}
      </Box>

      {/* Text */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body1"
          fontWeight={600}
          sx={{
            color: isActive ? colors.textPrimary : colors.textSecondary,
            transition: 'all 0.3s',
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: colors.textMuted, display: 'block' }}
        >
          {sublabel}
        </Typography>
      </Box>
    </Box>
  );
}

// ============================================================================
// Payment Gateway Card Component
// ============================================================================

interface GatewayCardProps {
  gateway: PaymentGateway;
  onToggle: () => void;
}

function GatewayCard({ gateway, onToggle }: GatewayCardProps) {
  const { colors } = useAdminTheme();
  const getIcon = () => {
    switch (gateway.type) {
      case 'card':
        return <PaymentIcon />;
      case 'eft':
        return <BankIcon />;
      case 'qr':
        return <QrCodeIcon />;
      case 'bnpl':
        return <PaymentIcon />;
      default:
        return <PaymentIcon />;
    }
  };

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        bgcolor: colors.bgCard,
        border: `1px solid ${gateway.enabled ? colors.success : colors.border}`,
        boxShadow: gateway.enabled
          ? `0 0 15px ${colors.successGlow}, inset 0 0 15px ${colors.successGlow}`
          : 'none',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          border: `1px solid ${gateway.enabled ? colors.success : colors.borderGlow}`,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {/* Icon + Status Light */}
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: gateway.enabled ? `${colors.success}20` : colors.bgElevated,
              border: `1px solid ${gateway.enabled ? colors.success : colors.border}`,
              '& svg': {
                fontSize: 24,
                color: gateway.enabled ? colors.success : colors.textMuted,
              },
            }}
          >
            {getIcon()}
          </Box>
          {/* Status Light */}
          <StatusDot
            sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              fontSize: 14,
              color: gateway.enabled ? colors.success : colors.error,
              filter: `drop-shadow(0 0 4px ${gateway.enabled ? colors.success : colors.error})`,
              animation: gateway.enabled ? 'pulse 2s infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.5 },
              },
            }}
          />
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="body1" fontWeight={600} sx={{ color: colors.textPrimary }}>
              {gateway.label}
            </Typography>
            <Chip
              label={gateway.enabled ? 'LIVE' : 'OFF'}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                bgcolor: gateway.enabled ? `${colors.success}20` : `${colors.error}20`,
                color: gateway.enabled ? colors.success : colors.error,
                border: `1px solid ${gateway.enabled ? colors.success : colors.error}40`,
              }}
            />
          </Box>
          <Typography variant="caption" sx={{ color: colors.textMuted }}>
            {gateway.description}
          </Typography>

          {/* Warning */}
          {gateway.warningMessage && gateway.enabled && (
            <Box
              sx={{
                mt: 1.5,
                p: 1,
                borderRadius: 1,
                bgcolor: `${colors.error}10`,
                border: `1px solid ${colors.error}40`,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
              }}
            >
              <WarningIcon sx={{ fontSize: 16, color: colors.error, mt: 0.25 }} />
              <Typography variant="caption" sx={{ color: colors.error }}>
                {gateway.warningMessage}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Toggle */}
        <Switch
          checked={gateway.enabled}
          onChange={onToggle}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: colors.success,
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: colors.success,
            },
          }}
        />
      </Box>
    </Box>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function SystemCore() {
  const { colors } = useAdminTheme();
  const [activeTab, setActiveTab] = useState<TabId>('financial');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Payment Gateways State
  const [gateways, setGateways] = useState<PaymentGateway[]>([]);

  // App Settings State
  const [appSettings, setAppSettings] = useState<AppSettingsState>({
    warnIfFarFromAddress: true,
    distanceThresholdKm: 5.0,
    enableBarcodeScanner: true,
    enableCompanionApp: false,
    useLoopDelivery: false,
    uberFallbackAfterMinutes: 20,
    uberDirectPriceCap: 110,
    uberFallbackAuto: false,
    loopNoCapacityRetries: 5,
    loopNoCapacityRetryMinutes: 3,
    trackingSmsFallbackMinutes: 3,
    smsProvider: 'twilio',
    refundNotificationEmail: 'finance@deeliver.co.za',
    refundCcEmails: [],
    enableAutoDearVoid: false,
    enableBarcodeAudit: false,
    auditIdleMinutes: 15,
    auditMissionTimeoutHours: 4,
    auditMissionSize: 10,
    auditPrioritizePopular: true,
    auditSkipNoImage: false,
    showRestrictedBadge: true,
    minimumAppVersionIos: '',
    minimumAppVersionAndroid: '',
    appStoreUrlIos: '',
    appStoreUrlAndroid: '',
    forceUpdateMessage: '',
  });

  // CC email input for adding new CC addresses
  const [ccEmailInput, setCcEmailInput] = useState('');

  // Uber Markup Divisor
  const [uberMarkupDivisor, setUberMarkupDivisor] = useState<number>(0.8275);
  const [uberMarkupFormula, setUberMarkupFormula] = useState('');
  const [uberMarkupSaving, setUberMarkupSaving] = useState(false);

  // ============================================================================
  // Data Fetching
  // ============================================================================

  const fetchPaymentGateways = useCallback(async () => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${config.API_URL}/admin/system-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to fetch payment settings');

      const data = await res.json();
      const paymentSettings = data.data
        .filter((s: any) => s.key.startsWith('payment_gateway_') && s.key.endsWith('_enabled'));

      const gatewayList: PaymentGateway[] = paymentSettings.map((setting: any) => {
        const name = setting.key.replace('payment_gateway_', '').replace('_enabled', '');
        return {
          key: setting.key,
          label: getGatewayLabel(name),
          description: getGatewayDescription(name),
          enabled: setting.value === 'true',
          warningMessage: getGatewayWarning(name),
          type: getGatewayType(name),
        };
      });

      setGateways(gatewayList);
    } catch (err: any) {
      setError(err.message);
    }
  }, []);

  const fetchAppSettings = useCallback(async () => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${config.API_URL}/admin/app-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to fetch app settings');

      const data = await res.json();
      setAppSettings({
        warnIfFarFromAddress: data.warnIfFarFromAddress,
        distanceThresholdKm: data.distanceThresholdKm,
        enableBarcodeScanner: data.enableBarcodeScanner ?? true,
        enableCompanionApp: data.enableCompanionApp ?? false,
        useLoopDelivery: data.useLoopDelivery ?? false,
        uberFallbackAfterMinutes: data.uberFallbackAfterMinutes ?? 20,
        uberDirectPriceCap: data.uberDirectPriceCap === undefined ? 110 : data.uberDirectPriceCap,
        uberFallbackAuto: data.uberFallbackAuto ?? false,
        loopNoCapacityRetries: data.loopNoCapacityRetries ?? 5,
        loopNoCapacityRetryMinutes: data.loopNoCapacityRetryMinutes ?? 3,
        trackingSmsFallbackMinutes: data.trackingSmsFallbackMinutes ?? 3,
        smsProvider: data.smsProvider ?? 'twilio',
        refundNotificationEmail: data.refundNotificationEmail ?? 'finance@deeliver.co.za',
        refundCcEmails: data.refundCcEmails ?? [],
        enableAutoDearVoid: data.enableAutoDearVoid ?? false,
        enableBarcodeAudit: data.enableBarcodeAudit ?? false,
        auditIdleMinutes: data.auditIdleMinutes ?? 15,
        auditMissionTimeoutHours: data.auditMissionTimeoutHours ?? 4,
        auditMissionSize: data.auditMissionSize ?? 10,
        auditPrioritizePopular: data.auditPrioritizePopular ?? true,
        auditSkipNoImage: data.auditSkipNoImage ?? false,
        showRestrictedBadge: data.showRestrictedBadge ?? true,
        minimumAppVersionIos: data.minimumAppVersionIos ?? '',
        minimumAppVersionAndroid: data.minimumAppVersionAndroid ?? '',
        appStoreUrlIos: data.appStoreUrlIos ?? '',
        appStoreUrlAndroid: data.appStoreUrlAndroid ?? '',
        forceUpdateMessage: data.forceUpdateMessage ?? '',
      });
    } catch (err: any) {
      Logger.error('Error fetching app settings:', err);
    }
  }, []);

  const fetchUberMarkup = useCallback(async () => {
    try {
      const token = await getValidToken();
      const res = await fetch(`${config.API_URL}/admin/tools/uber-markup`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setUberMarkupDivisor(data.data.divisor);
          setUberMarkupFormula(data.data.formula);
        }
      }
    } catch (err: any) {
      Logger.error('Error fetching Uber markup:', err);
    }
  }, []);

  // Load all data on mount
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchPaymentGateways(),
        fetchAppSettings(),
        fetchUberMarkup(),
      ]);
      setLoading(false);
    };

    loadData();
  }, [fetchPaymentGateways, fetchAppSettings, fetchUberMarkup]);

  // ============================================================================
  // Save Functions
  // ============================================================================

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const token = await getValidToken();

      // Save based on active tab
      if (activeTab === 'financial') {
        const settings: Record<string, string> = {};
        gateways.forEach((g) => {
          settings[g.key] = g.enabled ? 'true' : 'false';
        });

        const res = await fetch(`${config.API_URL}/admin/system-settings`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ settings }),
        });

        if (!res.ok) throw new Error('Failed to save payment settings');
      } else if (activeTab === 'inventory') {
        // Save refund/void settings (stored in app_settings table)
        const refundRes = await fetch(`${config.API_URL}/admin/app-settings`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            refundNotificationEmail: appSettings.refundNotificationEmail,
            refundCcEmails: appSettings.refundCcEmails,
            enableAutoDearVoid: appSettings.enableAutoDearVoid,
          }),
        });

        if (!refundRes.ok) throw new Error('Failed to save refund settings');

        // Save Uber markup divisor
        const uberRes = await fetch(`${config.API_URL}/admin/tools/uber-markup`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ divisor: uberMarkupDivisor }),
        });

        if (uberRes.ok) {
          const uberData = await uberRes.json();
          if (uberData.success) {
            setUberMarkupFormula(uberData.data.formula);
          }
        }
      } else if (activeTab === 'app') {
        const res = await fetch(`${config.API_URL}/admin/app-settings`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(appSettings),
        });

        if (!res.ok) {
          // The API refuses some settings with a reason (e.g. Loop delivery while
          // an active store has no branch id) — show it, not a generic failure.
          const body = await res.json().catch(() => ({}));
          throw new Error(body.userMessage || body.developerMessage || body.message || 'Failed to save app settings');
        }
        // Bust the shared useAppSettings cache so other pages see the new values
        invalidateAppSettings();
      }

      setSuccessMessage('Settings saved successfully!');
      setHasChanges(false);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: colors.bgDeepest,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress sx={{ color: colors.info }} />
      </Box>
    );
  }

  const tabConfigs = [
    { id: 'financial' as TabId, icon: <PaymentIcon />, label: 'Active Gateways', sublabel: 'Payment Gateways', color: colors.success },
    { id: 'inventory' as TabId, icon: <InventoryIcon />, label: 'Financial Settings', sublabel: 'Refunds & Uber Markup', color: colors.info },
    { id: 'app' as TabId, icon: <AppLogicIcon />, label: 'Application Logic', sublabel: 'App Settings', color: colors.purple },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: colors.bgDeepest,
        display: 'flex',
      }}
    >
      {/* Vertical Navigation Sidebar */}
      <Box
        sx={{
          width: 280,
          minHeight: '100vh',
          bgcolor: colors.bgCard,
          borderRight: `1px solid ${colors.border}`,
          p: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, p: 2 }}>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              color: colors.textPrimary,
              letterSpacing: '-0.02em',
              mb: 0.5,
            }}
          >
            SYSTEM CORE
          </Typography>
          <Typography variant="caption" sx={{ color: colors.textMuted }}>
            Central Configuration Panel
          </Typography>
        </Box>

        {/* Tabs */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {tabConfigs.map((tab) => (
            <NavTab
              key={tab.id}
              id={tab.id}
              icon={tab.icon}
              label={tab.label}
              sublabel={tab.sublabel}
              isActive={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
              glowColor={tab.color}
            />
          ))}
        </Box>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Save Button */}
        <Collapse in={hasChanges}>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleSave}
            disabled={saving}
            sx={{
              mt: 2,
              py: 1.5,
              bgcolor: colors.info,
              color: colors.textPrimary,
              fontWeight: 600,
              animation: hasChanges ? 'savePulse 2s infinite' : 'none',
              '@keyframes savePulse': {
                '0%, 100%': { boxShadow: `0 0 10px ${colors.infoGlow}` },
                '50%': { boxShadow: `0 0 25px ${colors.infoGlow}` },
              },
              '&:hover': {
                bgcolor: colors.info,
              },
            }}
          >
            {saving ? 'SYNCING...' : 'SAVE CHANGES'}
          </Button>
        </Collapse>
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          p: 4,
          overflow: 'auto',
        }}
      >
        {/* Glassmorphism Container */}
        <Box
          sx={{
            bgcolor: colors.bgGlass,
            backdropFilter: 'blur(12px)',
            borderRadius: 3,
            border: `1px solid ${colors.border}`,
            p: 4,
            minHeight: 'calc(100vh - 64px)',
          }}
        >
          {/* Error/Success Messages */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
              {successMessage}
            </Alert>
          )}

          {/* Active Gateways Tab */}
          {activeTab === 'financial' && (
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ color: colors.textPrimary, mb: 1 }}>
                Active Gateways
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 4 }}>
                Enable or disable payment methods. Changes take effect immediately on mobile apps.
              </Typography>

              <Box sx={{ display: 'grid', gap: 2 }}>
                {gateways.map((gateway) => (
                  <GatewayCard
                    key={gateway.key}
                    gateway={gateway}
                    onToggle={() => {
                      setGateways(gateways.map((g) =>
                        g.key === gateway.key ? { ...g, enabled: !g.enabled } : g
                      ));
                      setHasChanges(true);
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Financial Settings Tab */}
          {activeTab === 'inventory' && (
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ color: colors.textPrimary, mb: 1 }}>
                Financial Settings
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 4 }}>
                Configure refund notification and Uber Eats markup settings.
              </Typography>

              {/* Refund & Void Settings */}
              <Typography variant="h6" fontWeight={600} sx={{ color: colors.textPrimary, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ color: colors.info }} />
                Refund & Void Notifications
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted, mb: 3 }}>
                Configure where refund and sale void notifications are sent when orders are cancelled.
              </Typography>

              {/* Primary Refund Email */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: colors.textPrimary, mb: 2 }}>
                  Primary Notification Email
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={appSettings.refundNotificationEmail}
                  onChange={(e) => {
                    setAppSettings({ ...appSettings, refundNotificationEmail: e.target.value });
                    setHasChanges(true);
                  }}
                  placeholder="finance@deeliver.co.za"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      bgcolor: colors.bgElevated,
                      color: colors.textPrimary,
                      '& fieldset': { borderColor: colors.border },
                    },
                    '& .MuiInputLabel-root': { color: colors.textSecondary },
                  }}
                />
                <Typography variant="caption" sx={{ color: colors.textMuted, mt: 0.5, display: 'block' }}>
                  Main email address for all refund and void notifications
                </Typography>
              </Box>

              {/* CC Emails */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: colors.bgCard, border: `1px solid ${colors.border}`, mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: colors.textPrimary, mb: 2 }}>
                  CC Recipients
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                  <TextField
                    size="small"
                    value={ccEmailInput}
                    onChange={(e) => setCcEmailInput(e.target.value)}
                    placeholder="Add CC email address"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && ccEmailInput.trim() && ccEmailInput.includes('@')) {
                        setAppSettings({
                          ...appSettings,
                          refundCcEmails: [...appSettings.refundCcEmails, ccEmailInput.trim()],
                        });
                        setCcEmailInput('');
                        setHasChanges(true);
                      }
                    }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        bgcolor: colors.bgElevated,
                        color: colors.textPrimary,
                        '& fieldset': { borderColor: colors.border },
                      },
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={!ccEmailInput.trim() || !ccEmailInput.includes('@')}
                    onClick={() => {
                      setAppSettings({
                        ...appSettings,
                        refundCcEmails: [...appSettings.refundCcEmails, ccEmailInput.trim()],
                      });
                      setCcEmailInput('');
                      setHasChanges(true);
                    }}
                    sx={{
                      borderColor: colors.info,
                      color: colors.info,
                      '&:hover': { borderColor: colors.info, bgcolor: `${colors.info}20` },
                    }}
                  >
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {appSettings.refundCcEmails.map((email, idx) => (
                    <Chip
                      key={idx}
                      label={email}
                      onDelete={() => {
                        setAppSettings({
                          ...appSettings,
                          refundCcEmails: appSettings.refundCcEmails.filter((_, i) => i !== idx),
                        });
                        setHasChanges(true);
                      }}
                      deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                      sx={{
                        bgcolor: `${colors.info}20`,
                        color: colors.textPrimary,
                        border: `1px solid ${colors.info}40`,
                        '& .MuiChip-deleteIcon': { color: colors.textMuted, '&:hover': { color: colors.error } },
                      }}
                    />
                  ))}
                  {appSettings.refundCcEmails.length === 0 && (
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      No CC recipients added
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Auto Vend Void */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: colors.bgCard, border: `1px solid ${appSettings.enableAutoDearVoid ? colors.warning : colors.border}`, transition: 'all 0.3s' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: colors.textPrimary }}>
                      Auto-Void Vend Sales
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted }}>
                      {appSettings.enableAutoDearVoid
                        ? 'Cancelling an order does NOT void the sale — void it explicitly from the order page'
                        : 'Vend sale void requires manual confirmation during cancellation'}
                    </Typography>
                  </Box>
                  <Switch
                    checked={appSettings.enableAutoDearVoid}
                    onChange={(e) => {
                      setAppSettings({ ...appSettings, enableAutoDearVoid: e.target.checked });
                      setHasChanges(true);
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: colors.warning },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.warning },
                    }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 4, borderColor: colors.border }} />

              {/* Uber Eats Markup */}
              <Typography variant="h6" fontWeight={600} sx={{ color: colors.textPrimary, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <UberIcon sx={{ color: colors.info }} />
                Uber Eats Pricing
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textMuted, mb: 3 }}>
                Controls the markup divisor applied to Tier 1 prices when syncing products to Uber Eats.
                All Uber tools (Sync, Add Product, Reconciliation) use this value.
              </Typography>

              <Box sx={{ p: 2, borderRadius: 2, bgcolor: colors.bgCard, border: `1px solid ${colors.border}` }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      label="Markup Divisor"
                      type="number"
                      value={uberMarkupDivisor}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        if (!isNaN(val)) {
                          setUberMarkupDivisor(val);
                          setHasChanges(true);
                        }
                      }}
                      inputProps={{ step: 0.0001, min: 0.01, max: 1 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={{ color: colors.textMuted }}>
                            Tier1 /
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: colors.bgElevated,
                          color: colors.textPrimary,
                          '& fieldset': { borderColor: colors.border },
                        },
                        '& .MuiInputLabel-root': { color: colors.textSecondary },
                      }}
                    />
                    <Typography variant="caption" sx={{ color: colors.textMuted, mt: 0.5, display: 'block' }}>
                      Value between 0 and 1. E.g. 0.8275 gives ~20.8% markup
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: colors.bgElevated, border: `1px solid ${colors.border}` }}>
                    <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 0.5 }}>
                      Preview
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.textPrimary, fontFamily: 'monospace' }}>
                      R100.00 / {uberMarkupDivisor} = <strong>R{(100 / uberMarkupDivisor).toFixed(2)}</strong>
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.info, display: 'block', mt: 0.5 }}>
                      {((1 / uberMarkupDivisor - 1) * 100).toFixed(1)}% markup on Tier 1 price
                    </Typography>
                    {uberMarkupFormula && (
                      <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mt: 0.5 }}>
                        {uberMarkupFormula}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {(uberMarkupDivisor <= 0 || uberMarkupDivisor >= 1) && (
                  <Alert severity="error" sx={{ mt: 2, bgcolor: `${colors.error}15`, color: colors.textPrimary, border: `1px solid ${colors.error}40` }}>
                    Divisor must be between 0 and 1 (exclusive)
                  </Alert>
                )}
              </Box>
            </Box>
          )}

          {/* Application Logic Tab */}
          {activeTab === 'app' && (
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ color: colors.textPrimary, mb: 1 }}>
                Application Logic
              </Typography>
              <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 4 }}>
                Configure app-wide settings that apply to all users across all stores.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* GPS Warning */}
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <GpsIcon sx={{ color: colors.purple }} />
                    <Typography variant="h6" fontWeight={600} sx={{ color: colors.textPrimary }}>
                      GPS Location Warning
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                        Warn users if they're far from their delivery address at checkout
                      </Typography>
                    </Box>
                    <Switch
                      checked={appSettings.warnIfFarFromAddress}
                      onChange={(e) => {
                        setAppSettings({ ...appSettings, warnIfFarFromAddress: e.target.checked });
                        setHasChanges(true);
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: colors.purple },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.purple },
                      }}
                    />
                  </Box>
                  <TextField
                    label="Distance Threshold"
                    type="number"
                    value={appSettings.distanceThresholdKm}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (!isNaN(value) && value >= 0.1 && value <= 100) {
                        setAppSettings({ ...appSettings, distanceThresholdKm: value });
                        setHasChanges(true);
                      }
                    }}
                    disabled={!appSettings.warnIfFarFromAddress}
                    size="small"
                    InputProps={{
                      endAdornment: <InputAdornment position="end">km</InputAdornment>,
                    }}
                    sx={{
                      maxWidth: 200,
                      '& .MuiOutlinedInput-root': {
                        bgcolor: colors.bgElevated,
                        color: colors.textPrimary,
                        '& fieldset': { borderColor: colors.border },
                      },
                      '& .MuiInputLabel-root': { color: colors.textSecondary },
                    }}
                  />
                </Box>

                {/* Barcode Scanner */}
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <ScannerIcon sx={{ color: colors.purple }} />
                    <Typography variant="h6" fontWeight={600} sx={{ color: colors.textPrimary }}>
                      Barcode Scanner
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                        {appSettings.enableBarcodeScanner ? 'Scanner enabled' : 'Scanner disabled'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.textMuted }}>
                        {appSettings.enableBarcodeScanner
                          ? 'Barcode scanner button visible in search bar'
                          : 'Barcode scanner button hidden from search bar'}
                      </Typography>
                    </Box>
                    <Switch
                      checked={appSettings.enableBarcodeScanner}
                      onChange={(e) => {
                        setAppSettings({ ...appSettings, enableBarcodeScanner: e.target.checked });
                        setHasChanges(true);
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: colors.purple },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.purple },
                      }}
                    />
                  </Box>
                </Box>

                {/* Companion App */}
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: colors.bgCard,
                    border: `1px solid ${appSettings.enableCompanionApp ? colors.success : colors.border}`,
                    boxShadow: appSettings.enableCompanionApp
                      ? `0 0 15px ${colors.successGlow}, inset 0 0 15px ${colors.successGlow}`
                      : 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CompanionIcon sx={{ color: appSettings.enableCompanionApp ? colors.success : colors.purple }} />
                    <Typography variant="h6" fontWeight={600} sx={{ color: colors.textPrimary }}>
                      Companion App
                    </Typography>
                    <Chip
                      label={appSettings.enableCompanionApp ? 'ACTIVE' : 'OFF'}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        bgcolor: appSettings.enableCompanionApp ? `${colors.success}20` : `${colors.error}20`,
                        color: appSettings.enableCompanionApp ? colors.success : colors.error,
                        border: `1px solid ${appSettings.enableCompanionApp ? colors.success : colors.error}40`,
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                        {appSettings.enableCompanionApp ? 'Picking features enabled' : 'Picking features disabled'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.textMuted }}>
                        {appSettings.enableCompanionApp
                          ? 'Picker status on orders, order chat, barcode reviews, proof photos, and Mission Control Centre visible in Inventory Tools'
                          : 'All companion picking features and Mission Control Centre hidden from admin panel'}
                      </Typography>
                    </Box>
                    <Switch
                      checked={appSettings.enableCompanionApp}
                      onChange={(e) => {
                        setAppSettings({ ...appSettings, enableCompanionApp: e.target.checked });
                        setHasChanges(true);
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: colors.success },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.success },
                      }}
                    />
                  </Box>

                </Box>

                {/* Delivery Provider (Loop / Uber Direct) */}
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: colors.bgCard,
                    border: `1px solid ${appSettings.useLoopDelivery ? colors.success : colors.border}`,
                    boxShadow: appSettings.useLoopDelivery
                      ? `0 0 15px ${colors.successGlow}, inset 0 0 15px ${colors.successGlow}`
                      : 'none',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CompanionIcon sx={{ color: appSettings.useLoopDelivery ? colors.success : colors.purple }} />
                    <Typography variant="h6" fontWeight={600} sx={{ color: colors.textPrimary }}>
                      Delivery Provider
                    </Typography>
                    <Chip
                      label={appSettings.useLoopDelivery ? 'LOOP' : 'UBER'}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        bgcolor: appSettings.useLoopDelivery ? `${colors.success}20` : `${colors.error}20`,
                        color: appSettings.useLoopDelivery ? colors.success : colors.error,
                        border: `1px solid ${appSettings.useLoopDelivery ? colors.success : colors.error}40`,
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                        {appSettings.useLoopDelivery ? 'Loop (Takealot Driver Service)' : 'Uber Direct'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.textMuted }}>
                        {appSettings.useLoopDelivery
                          ? 'Takealot zones are dispatched via Loop / Takealot Driver Service. Uber Direct zones always use Uber Direct.'
                          : 'Every delivery is dispatched via Uber Direct (default)'}
                      </Typography>
                    </Box>
                    <Switch
                      checked={appSettings.useLoopDelivery}
                      onChange={(e) => {
                        setAppSettings({ ...appSettings, useLoopDelivery: e.target.checked });
                        setHasChanges(true);
                      }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: colors.success },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.success },
                      }}
                    />
                  </Box>

                  {/* Uber Direct zone price cap (backend: deliveries/services/courier-choice.js) */}
                  <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${colors.border}` }} data-testid="uber-direct-price-cap">
                    <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                      Uber Direct price cap
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 1.5 }}>
                      Addresses outside the Takealot zones but inside a store&apos;s Uber Direct zone are delivered by Uber Direct only when Uber&apos;s quote (incl. VAT) is at or under this. Over it, checkout says we can&apos;t deliver there right now. Leave empty for no cap.
                    </Typography>
                    <TextField
                      id="uber-direct-price-cap"
                      label="Highest Uber quote"
                      type="number"
                      value={appSettings.uberDirectPriceCap ?? ''}
                      onChange={(e) => {
                        const raw = e.target.value.trim();
                        if (raw === '') {
                          setAppSettings({ ...appSettings, uberDirectPriceCap: null });
                          setHasChanges(true);
                          return;
                        }
                        const value = Number(raw);
                        if (Number.isFinite(value) && value >= 1 && value <= 2000) {
                          setAppSettings({ ...appSettings, uberDirectPriceCap: Math.round(value * 100) / 100 });
                          setHasChanges(true);
                        }
                      }}
                      size="small"
                      inputProps={{ min: 1, max: 2000, step: 1, 'aria-label': 'Uber Direct price cap in Rand including VAT' }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R</InputAdornment>,
                        endAdornment: <InputAdornment position="end">incl. VAT</InputAdornment>,
                      }}
                      sx={{
                        maxWidth: 240,
                        '& .MuiOutlinedInput-root': { bgcolor: colors.bgElevated, color: colors.textPrimary, '& fieldset': { borderColor: colors.border } },
                        '& .MuiInputLabel-root': { color: colors.textSecondary },
                      }}
                    />
                  </Box>

                  {/* Uber Direct fallback for Loop orders (backend: deliveries/services/uber-fallback.js) */}
                  {appSettings.useLoopDelivery && (
                    <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="uber-fallback-settings">
                      {/* Loop "no drivers" at booking (backend: loop-delivery-service.js). Loop is asked
                          about capacity only when the delivery is created, never at coverage. */}
                      <Box data-testid="loop-retry-settings">
                        <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                          No Loop drivers when booking
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 1.5 }}>
                          When a packed order is sent to Loop and Loop has no drivers free, try again this many times, this far apart. After that the order stays packed for the Uber Direct fallback below.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <TextField
                            label="Retries"
                            type="number"
                            value={appSettings.loopNoCapacityRetries}
                            onChange={(e) => {
                              const value = parseInt(e.target.value, 10);
                              if (!isNaN(value) && value >= 0 && value <= 20) {
                                setAppSettings({ ...appSettings, loopNoCapacityRetries: value });
                                setHasChanges(true);
                              }
                            }}
                            size="small"
                            inputProps={{ min: 0, max: 20, 'aria-label': 'Loop no-capacity retries' }}
                            sx={{
                            maxWidth: 200,
                            '& .MuiOutlinedInput-root': { bgcolor: colors.bgElevated, color: colors.textPrimary, '& fieldset': { borderColor: colors.border } },
                            '& .MuiInputLabel-root': { color: colors.textSecondary },
                          }}
                          />
                          <TextField
                            label="Every"
                            type="number"
                            value={appSettings.loopNoCapacityRetryMinutes}
                            onChange={(e) => {
                              const value = parseInt(e.target.value, 10);
                              if (!isNaN(value) && value >= 1 && value <= 15) {
                                setAppSettings({ ...appSettings, loopNoCapacityRetryMinutes: value });
                                setHasChanges(true);
                              }
                            }}
                            size="small"
                            inputProps={{ min: 1, max: 15, 'aria-label': 'Minutes between Loop retries' }}
                            InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                            sx={{
                            maxWidth: 200,
                            '& .MuiOutlinedInput-root': { bgcolor: colors.bgElevated, color: colors.textPrimary, '& fieldset': { borderColor: colors.border } },
                            '& .MuiInputLabel-root': { color: colors.textSecondary },
                          }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mt: 1 }}>
                          {appSettings.loopNoCapacityRetries === 0
                            ? 'No retries: ops is alerted straight away.'
                            : `Keeps trying for about ${appSettings.loopNoCapacityRetries * appSettings.loopNoCapacityRetryMinutes} minutes.`}
                        </Typography>
                      </Box>
                      <Box sx={{ pt: 2, borderTop: `1px solid ${colors.border}` }}>
                        <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                          Uber Direct fallback
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.textMuted }}>
                          When Loop gives up on an order (returned, address, abandoned) or has no driver after the time below, the order can be sent with Uber Direct instead.
                        </Typography>
                      </Box>
                      <TextField
                        label="No driver after"
                        type="number"
                        value={appSettings.uberFallbackAfterMinutes}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10);
                          if (!isNaN(value) && value >= 5 && value <= 180) {
                            setAppSettings({ ...appSettings, uberFallbackAfterMinutes: value });
                            setHasChanges(true);
                          }
                        }}
                        size="small"
                        inputProps={{ min: 5, max: 180, 'aria-label': 'Minutes without a Loop driver' }}
                        InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                        sx={{
                          maxWidth: 200,
                          '& .MuiOutlinedInput-root': { bgcolor: colors.bgElevated, color: colors.textPrimary, '& fieldset': { borderColor: colors.border } },
                          '& .MuiInputLabel-root': { color: colors.textSecondary },
                        }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                            Switch automatically
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.textMuted }}>
                            {appSettings.uberFallbackAuto
                              ? 'Orders go to Uber Direct by themselves (checked every minute)'
                              : 'Off: the order page offers a "Send with Uber Direct" button'}
                          </Typography>
                        </Box>
                        <Switch
                          checked={appSettings.uberFallbackAuto}
                          onChange={(e) => {
                            setAppSettings({ ...appSettings, uberFallbackAuto: e.target.checked });
                            setHasChanges(true);
                          }}
                          inputProps={{ 'aria-label': 'Switch to Uber Direct automatically' }}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: colors.success },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.success },
                          }}
                        />
                      </Box>

                      <Box sx={{ pt: 2, borderTop: `1px solid ${colors.border}` }}>
                        <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                          Tracking link by SMS
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 1.5 }}>
                          Loop customers get the tracking link by WhatsApp. If it isn&apos;t delivered within this time (phone off, no WhatsApp), the link goes by SMS instead, once.
                        </Typography>
                        <TextField
                          label="WhatsApp not delivered after"
                          type="number"
                          value={appSettings.trackingSmsFallbackMinutes}
                          onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            if (!isNaN(value) && value >= 1 && value <= 30) {
                              setAppSettings({ ...appSettings, trackingSmsFallbackMinutes: value });
                              setHasChanges(true);
                            }
                          }}
                          size="small"
                          inputProps={{ min: 1, max: 30, 'aria-label': 'Minutes before the tracking SMS' }}
                          InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                          sx={{
                            maxWidth: 240,
                            '& .MuiOutlinedInput-root': { bgcolor: colors.bgElevated, color: colors.textPrimary, '& fieldset': { borderColor: colors.border } },
                            '& .MuiInputLabel-root': { color: colors.textSecondary },
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>

                {/* SMS provider (backend: common/infra/sms/sms-sender.js) */}
                <Box
                  sx={{ p: 3, borderRadius: 2, bgcolor: colors.bgCard, border: `1px solid ${colors.border}` }}
                  data-testid="sms-provider"
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                      <Typography variant="h6" fontWeight={600} sx={{ color: colors.textPrimary }}>
                        SMS provider
                      </Typography>
                      <Typography variant="body2" sx={{ color: colors.textPrimary, mt: 0.5 }}>
                        {appSettings.smsProvider === 'bulksms' ? 'BulkSMS' : 'Twilio'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.textMuted }}>
                        OTP codes, tracking links and other SMS. BulkSMS costs about R0.32 an SMS against Twilio&apos;s ~R2.80; if BulkSMS fails, that message goes by Twilio. WhatsApp always uses Twilio.
                      </Typography>
                    </Box>
                    <Switch
                      checked={appSettings.smsProvider === 'bulksms'}
                      onChange={(e) => {
                        setAppSettings({ ...appSettings, smsProvider: e.target.checked ? 'bulksms' : 'twilio' });
                        setHasChanges(true);
                      }}
                      inputProps={{ 'aria-label': 'Send SMS with BulkSMS' }}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: colors.success },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.success },
                      }}
                    />
                  </Box>
                </Box>

                {/* Restricted Products */}
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <RestrictedIcon sx={{ color: colors.error }} />
                    <Typography variant="h6" fontWeight={600} sx={{ color: colors.textPrimary }}>
                      Restricted Products
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 3 }}>
                    Control how age-restricted items (liquor, vapes) are displayed to customers
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box>
                        <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                          Show Under 18 Badge
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.textMuted }}>
                          Display animated age restriction badge on cart items
                        </Typography>
                      </Box>
                      <Switch
                        checked={appSettings.showRestrictedBadge}
                        onChange={(e) => {
                          setAppSettings({ ...appSettings, showRestrictedBadge: e.target.checked });
                          setHasChanges(true);
                        }}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: colors.error },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.error },
                        }}
                      />
                    </Box>

                  </Box>
                </Box>

                {/* Force update — the API answers 426 to app builds below the minimum
                    (src/common/infra/app-version/app-version-gate.js). Blank = off. */}
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    bgcolor: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                  }}
                  data-testid="force-update-card"
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <UpdateIcon sx={{ color: colors.warning }} />
                    <Typography variant="h6" fontWeight={600} sx={{ color: colors.textPrimary }}>
                      Force Update
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 3 }}>
                    App builds below the minimum version are shown a blocking "Update required" screen with the store link.
                    Leave a minimum blank to switch it off for that platform. Any minimum also locks out the old app, which sends no version.
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <TextField
                      label="Minimum iOS version"
                      placeholder="e.g. 2.3.0"
                      value={appSettings.minimumAppVersionIos}
                      onChange={(e) => { setAppSettings({ ...appSettings, minimumAppVersionIos: e.target.value.trim() }); setHasChanges(true); }}
                      size="small"
                      inputProps={{ 'data-testid': 'min-version-ios' }}
                    />
                    <TextField
                      label="Minimum Android version"
                      placeholder="e.g. 2.3.0"
                      value={appSettings.minimumAppVersionAndroid}
                      onChange={(e) => { setAppSettings({ ...appSettings, minimumAppVersionAndroid: e.target.value.trim() }); setHasChanges(true); }}
                      size="small"
                      inputProps={{ 'data-testid': 'min-version-android' }}
                    />
                    <TextField
                      label="App Store link (iOS)"
                      placeholder="https://apps.apple.com/za/app/deeliver/id…"
                      value={appSettings.appStoreUrlIos}
                      onChange={(e) => { setAppSettings({ ...appSettings, appStoreUrlIos: e.target.value.trim() }); setHasChanges(true); }}
                      size="small"
                    />
                    <TextField
                      label="Google Play link (Android)"
                      placeholder="https://play.google.com/store/apps/details?id=…"
                      value={appSettings.appStoreUrlAndroid}
                      onChange={(e) => { setAppSettings({ ...appSettings, appStoreUrlAndroid: e.target.value.trim() }); setHasChanges(true); }}
                      size="small"
                    />
                    <TextField
                      label="Message shown to the customer"
                      placeholder="Please update the Deeliver app to continue."
                      value={appSettings.forceUpdateMessage}
                      onChange={(e) => { setAppSettings({ ...appSettings, forceUpdateMessage: e.target.value }); setHasChanges(true); }}
                      size="small"
                      multiline
                      minRows={2}
                      sx={{ gridColumn: { md: '1 / -1' } }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ============================================================================
// Helper Functions
// ============================================================================

function getGatewayLabel(name: string): string {
  const labels: Record<string, string> = {
    paygate: 'PayGate',
    ozow: 'Ozow',
    zapper: 'Zapper',
    snapscan: 'SnapScan',
    payflex: 'Payflex',
    payjustnow: 'PayJustNow',
    // PayFast carries five methods, each toggled independently so one can be
    // disabled without touching the other four.
    applepay: 'Apple Pay',
    googlepay: 'Google Pay',
    masterpass: 'Masterpass',
    mobicred: 'Mobicred',
    payfasteft: 'PayFast EFT',
  };
  return labels[name] || name.toUpperCase();
}

function getGatewayDescription(name: string): string {
  const descriptions: Record<string, string> = {
    paygate: 'Credit/Debit cards (Visa, Mastercard, Amex, Diners)',
    ozow: 'Instant bank-to-bank EFT transfers',
    zapper: 'Universal QR code payments',
    snapscan: 'SnapScan QR code payments',
    payflex: 'Buy now, pay later installments',
    payjustnow: 'Buy now, pay later installments',
    applepay: 'Apple Pay wallet, via PayFast (iOS devices only)',
    googlepay: 'Google Pay wallet, via PayFast (Android devices only)',
    masterpass: 'Masterpass wallet, via PayFast',
    mobicred: 'Mobicred revolving credit, via PayFast',
    // Deliberately names the gateway: Ozow above is also EFT, and the two are
    // indistinguishable to a shopper if both are enabled at once.
    payfasteft: 'Bank EFT via PayFast — separate from Ozow above',
  };
  return descriptions[name] || '';
}

function getGatewayWarning(name: string): string | undefined {
  const warnings: Record<string, string> = {
    applepay: 'Only renders on iOS devices. Unverified in the app WebView until a live device test passes.',
    googlepay: 'Only renders on Android devices. Unverified in the app WebView until a live device test passes.',
    payfasteft: 'Ozow also offers EFT. Enabling both shows the customer two near-identical options.',
  };
  return warnings[name];
}

function getGatewayType(name: string): 'card' | 'eft' | 'qr' | 'bnpl' {
  if (name === 'paygate') return 'card';
  if (name === 'ozow') return 'eft';
  if (name === 'zapper' || name === 'snapscan') return 'qr';
  if (name === 'payflex' || name === 'payjustnow') return 'bnpl';
  if (name === 'applepay' || name === 'googlepay') return 'card';
  // Masterpass in South Africa IS Scan to Pay — a QR product, not a card
  // wallet. Our own migration names it that way ('Masterpass/Scan to Pay'),
  // so it groups with zapper and snapscan.
  if (name === 'masterpass') return 'qr';
  if (name === 'mobicred') return 'bnpl';
  if (name === 'payfasteft') return 'eft';
  return 'card';
}

export default SystemCore;
